import { UserTerms, WelcomeFeatureSettings } from '@centecomm/portal-service';
import { useCurrentUser, useUsers } from '@centecomm/portal-service-react';
import WelcomeModal from 'Features/Business/Welcome/Pages/WelcomeModal';
import React from 'react';
import { useDispatch } from 'react-redux';
import { LoadingSpinner } from 'Shared/PageElements/LoadingSpinner';
import { VersionUpdateNotification } from 'Shared/VersionUpdate/VersionUpdateNotification';
import { globalMessageActionCreators } from 'State';
import useSettingsFromState from 'State/Settings/Hooks/useSettingsFromState';
import useFullStoryIdentify from 'Vendors/FullStory/Hooks/useIdentify';
import PecoHotjarSnippet from './CompanySpecific/PECO/PecoHotjarSnippet';
import useI18nFeature from './i18n/Hooks/useI18nFeature';
import OwnerOperatorRoot from './RootRoutes/OwnerOperatorRoot';
import TenantRoot from './RootRoutes/TenantRoot';

function RootApp(): React.ReactElement {
    const { appLanguageFullySet } = useI18nFeature();
    const dispatch = useDispatch();
    const { currentUser, isTenant, currentUserId: userId } = useCurrentUser();
    const { getUserTerms } = useUsers();
    const { isLoaded: settingsLoaded, hasError: settingsErrors, settings } = useSettingsFromState();
    const [termsAccepted, setTermsAccepted] = React.useState<boolean>(false);
    const [termsLoading, setTermsLoading] = React.useState<boolean>(true);
    const [userTerms, setUserTerms] = React.useState<UserTerms>({} as UserTerms);

    React.useEffect(() => {
        (async () => {
            const terms = await getUserTerms(userId as number);
            setUserTerms(terms);
            setTermsAccepted(terms.termsAcceptedOn !== undefined && terms.termsAcceptedOn !== null);
            setTermsLoading(false);
        })();
    }, []);

    React.useEffect(() => {
        if (settingsErrors) {
            dispatch(globalMessageActionCreators.addErrorMessage('Unable to retrieve application settings'));
        }
    }, [settingsErrors]);

    useFullStoryIdentify(currentUser);

    if (!settingsLoaded || !appLanguageFullySet) return <LoadingSpinner />;

    return (
        <>
            {isTenant ? (
                <>
                    {!termsAccepted && (
                        <WelcomeModal
                            isLoading={termsLoading}
                            userTerms={userTerms}
                            userSettings={settings?.welcome as WelcomeFeatureSettings}
                        />
                    )}
                    <TenantRoot />
                </>
            ) : (
                <>
                    {!termsAccepted && <WelcomeModal isLoading={termsLoading} userTerms={userTerms} />}
                    <OwnerOperatorRoot />
                </>
            )}
            {/* TODO: Replace this code with the future company-specific feature flags (AdminConfig extension) */}
            <PecoHotjarSnippet />

            {/* Version update notification - shows when new version is available */}
            <VersionUpdateNotification
                enabled
                checkInterval={5 * 60 * 1000} // Check every 5 minutes
                position="top"
            />
        </>
    );
}

export default RootApp;
